import { useQuery, UseQueryResult } from 'react-query';
import { GroupedPolicyList, PoliciesStatus, PolicyAgent, PolicyList, PolicyWithAgent } from '../types/policy.types';
import PolicyService from '../services/policy.service';
import SalesPortfolioService from 'modules/sales-policies/services/sales-portfolio.service';
import { Portfolio } from 'modules/sales-policies/types/sales-portfolio.interfaces';

export const useJourneyRedirectStatus = (): UseQueryResult<{ [key: string]: boolean }, Error> => {
  return useQuery(['redirect-status'], () => PolicyService.getJourneyRedirectStatus());
};

export const useFilteredPolicy = (
  status: string,
  pageNo: number,
  pageSize: number
): UseQueryResult<PolicyList, Error> => {
  return useQuery(
    [`policy-lists-${status}`, pageSize, pageNo],
    () => PolicyService.getFilteredPolicies(status, pageNo, pageSize),
    {
      keepPreviousData: pageNo !== 1,
    }
  );
};

export const useGroupedFilteredPolicies = (
  status: string,
  pageNo: number,
  pageSize: number
): UseQueryResult<GroupedPolicyList, Error> => {
  return useQuery(
    [`grouped-policy-lists-${status}`, pageSize, pageNo],
    () => PolicyService.getGroupedFilteredPolicies(status, pageNo, pageSize),
    {
      keepPreviousData: pageNo !== 1,
    }
  );
};

export const useAllPolicyStatus = (pageNo = 1, pageSize = 10): UseQueryResult<PoliciesStatus, Error> => {
  return useQuery(['customer_policies'], () => PolicyService.getPoliciesStatus(pageNo, pageSize));
};

export const usePolicyAgent = (partyId: string): UseQueryResult<PolicyAgent, Error> => {
  return useQuery(['policy-agent', partyId], () => PolicyService.getPolicyAgent(partyId));
};

export const usePolicyWithAgent = (policyNo: string): UseQueryResult<PolicyWithAgent, Error> => {
  return useQuery(['policy-with-agent', policyNo], () => PolicyService.getPolicyWithAgent(policyNo));
};

interface QuoteData {
  redFlagStatus: boolean;
  quoteLines: Portfolio[];
  containsNonQBProducts: boolean;
}

export const useRedFlagOrQuoteLinesForBuy = (
  policyNo: string,
  productId: string | undefined
): UseQueryResult<QuoteData, Error> => {
  return useQuery(
    ['red-flag-and-quote-lines', policyNo],
    async () => {
      const buyQuoteData = {
        redFlagStatus: false,
        quoteLines: [] as Portfolio[],
        containsNonQBProducts: false,
      };

      const redFlagStatusRes = await PolicyService.getRedFlagPolicyStatus();

      if (redFlagStatusRes && redFlagStatusRes.data[0].submissionData.isFlaggedRed) {
        buyQuoteData.redFlagStatus = redFlagStatusRes.data[0].submissionData.isFlaggedRed;

        return buyQuoteData;
      }

      const quoteLinesRes = await SalesPortfolioService.getAllQuoteLines(policyNo, productId ? productId : 'PR');

      buyQuoteData.quoteLines = quoteLinesRes.payload;
      buyQuoteData.containsNonQBProducts = quoteLinesRes.containsNonQBProducts;

      return buyQuoteData;
    },
    {
      enabled: false,
    }
  );
};
